/**
 * App.tsx
 * --------
 * What: Root component that composes global providers (config, fonts, theming,
 * auth, data, subscription, image cache, legal) and renders navigation.
 * Why: Establishes application-wide context boundaries and handles global states
 * like maintenance mode and offline/server-down fallbacks before showing UI.
 */
import React from 'react';
import { ActivityIndicator, View } from 'react-native';
import { AuthProvider } from './lib/AuthContext';
import { FontProvider } from './app/fonts/FontProvider';
import { NavigationProvider } from './app/navigation/NavigationContext';
import { UnifiedNavigator } from './app/navigation/UnifiedNavigator';
import { SubscriptionProvider } from './lib/SubscriptionContext';
import { DataProvider } from './lib/DataContext';
import { ThemeProvider } from './lib/ThemeContext';
import { ImageCacheProvider } from './lib/ImageCacheContext';
import { ConfigProvider, useConfig } from './lib/ConfigContext';
import MaintenanceScreen from './app/screens/MaintenanceScreen';
import UnavailableScreen from './app/screens/UnavailableScreen';
import { LegalProvider } from './lib/LegalContext';

export default function App() {
  
  return (
    <ConfigProvider>
      <FontProvider>
        <AppWithConfig />
      </FontProvider>
    </ConfigProvider>
  );
}

function AppWithConfig() {
  const { config, loading, status } = useConfig();

  // Disable noisy console logs immediately if remote flag is off
  const wantVerbose = config?.App?.verboseLogging !== false;
  (globalThis as any).__VERBOSE_LOGS__ = wantVerbose; // expose flag globally

  if (!wantVerbose) {
    // Patch only once to avoid unnecessary overrides during re-renders
    if (!(console as any).__lmhPatched) {
      console.log = () => {};
      console.debug = () => {};
      (console as any).__lmhPatched = true;
    }
  }

  if (loading) {
    // Simple fullscreen loader
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: '#000' }}>
        <ActivityIndicator size="large" color="#fff" />
      </View>
    );
  }

  if (status === 'offline' || status === 'server_down') {
    return (
      <ThemeProvider>
        <UnavailableScreen type={status} />
      </ThemeProvider>
    );
  }

  if (config?.App?.maintenanceMode) {
    return (
      <ThemeProvider>
        <MaintenanceScreen />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider>
      <AuthProvider>
        <SubscriptionProvider>
          <ImageCacheProvider>
            <DataProvider>
              <LegalProvider>
                <NavigationProvider>
                  <UnifiedNavigator />
                </NavigationProvider>
              </LegalProvider>
            </DataProvider>
          </ImageCacheProvider>
        </SubscriptionProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}
